import type { PostureAnalysis } from '@/lib/types';
import type { CaptureSequence } from '@/components/posture-analysis/types';
import {
  getLaymanGroundingSide,
  getLaymanKeyMetric,
  getLaymanLateralBadge,
  getLaymanPatternChipLabel,
  getLaymanPatternLabel,
  getLaymanPhase1Goal,
  getLaymanReasoningLine,
  getLaymanSignal,
  getLaymanStrategyLabel,
  LAYMAN_CLASSIFICATION_UI,
} from './classification-display-labels';

export { LAYMAN_CLASSIFICATION_UI };
import { getDrillsForPattern, type DrillEntry } from './drills';

export const SELF_ASSESSMENT_VIEWS = ['Anterior', 'Right'] as const satisfies readonly CaptureSequence[];

export type SelfAssessmentView = (typeof SELF_ASSESSMENT_VIEWS)[number];

export interface SummaryCardInfo {
  label: string;
  explanation: string;
}

export const ORIENTATION_LABELS: Record<string, SummaryCardInfo> = {
  swayback: {
    label: 'Swayback',
    explanation:
      'Your body weight has shifted too far forward over your feet. To stop yourself from falling face-first, your body subconsciously clenches the back side of your hips (your glutes) and grips hard with your six-pack muscles. This shoves your hips way out in front of your body, rounds your upper back backward to counterbalance the weight, and forces your knees to bend to act as brakes. Essentially, your torso and hips are being squeezed tight from front to back like a pancake.',
  },
  anteriorPelvicTilt: {
    label: 'Anterior Pelvic Tilt (APT)',
    explanation:
      'Your body\'s center of gravity has shifted forward onto your toes, and your hips have lost some of their natural ability to rotate. To compensate so you can still put force into the ground to move, your lower back tightens up and points your hip sockets down at the floor, arching your back. Because you are falling forward, your body locks your knees out backwards to act as brakes, and your lower ribs flare up to keep you upright against gravity.',
  },
  anteriorPelvicTiltPlc: {
    label: 'Anterior Pelvic Tilt (APT) moving to Swayback',
    explanation:
      'This is the next level of an Anterior Pelvic Tilt Pattern. Your pelvis was tilted forward, but your body weight kept shifting so far forward that your brain panicked. To act as an emergency brake and stop you from falling, your body started aggressively clenching the very bottom back side of your hips. You still have an arched lower back, but now your hips are glued shut in the back, which is creating a further pushing forward of the lower body\'s center of gravity.',
  },
  neutral: {
    label: 'Neutral / balanced posture',
    explanation:
      'Your posture is within a healthy neutral range. Your foundation is solid — the goal now is to maintain and reinforce it through load-bearing and mobility work.',
  },
};

export const FHP_LABEL: SummaryCardInfo = {
  label: 'Forward Head Posture',
  explanation:
    'Your head being pushed forward is actually a compensation strategy. Because your lower body and ribcage are shifted out of their natural alignment, your head has to jut forward just to counterbalance your weight so you don\'t fall over backward. Additionally, because your ribcage is squeezed tight, your body pushes your head forward to open your airway and uses your neck muscles to help pull air into your lungs every time you breathe.',
};

export const LATERAL_LABELS: Record<string, SummaryCardInfo> = {
  left: {
    label: 'Leaning slightly left',
    explanation:
      "Your weight distribution shows a mild lean to the left. This is often related to your body's center of gravity being shifted in that direction to better manage gravity and other compensations with how your body manages repetitive movements in your life and prior/current injuries. This is most effectively fixed after addressing your overall front-to-back posture first.",
  },
  right: {
    label: 'Leaning slightly right',
    explanation:
      "Your weight distribution shows a mild lean to the right. This is often related to your body's center of gravity being shifted in that direction to better manage gravity and other compensations with how your body manages repetitive movements in your life and prior/current injuries. This is most effectively fixed after addressing your overall front-to-back posture first.",
  },
  neutral: {
    label: 'Well-balanced left to right',
    explanation:
      'Your lateral balance is solid. Keep up the symmetrical loading patterns — single-leg exercises will help maintain this over time.',
  },
};

export function getOrientationInfo(pattern: string | undefined): SummaryCardInfo {
  if (!pattern) return ORIENTATION_LABELS.neutral;
  const pat = pattern.toLowerCase().replace(/[\s-_()]/g, '');
  if (pat === 'sb' || pat.includes('sway')) return ORIENTATION_LABELS.swayback;
  if (pat.includes('posteriorlowercompression') || pat.includes('plc')) return ORIENTATION_LABELS.anteriorPelvicTiltPlc;
  if (pat === 'apt' || pat.includes('anterior')) return ORIENTATION_LABELS.anteriorPelvicTilt;
  if (pat === 'n' || pat.includes('neutral') || pat.includes('stack')) return ORIENTATION_LABELS.neutral;
  return ORIENTATION_LABELS.neutral;
}

const LATERAL_NEUTRAL_WITHOUT_EXERCISES: SummaryCardInfo = {
  label: 'Well-balanced left to right',
  explanation:
    'Your lateral balance is solid. Keep up symmetrical loading patterns to maintain this over time.',
};

export function getLateralInfo(
  lateralized: string | undefined,
  options?: { hideExercises?: boolean },
): SummaryCardInfo {
  const hide = options?.hideExercises ?? true;
  const neutral = hide ? LATERAL_NEUTRAL_WITHOUT_EXERCISES : LATERAL_LABELS.neutral;
  if (!lateralized) return neutral;
  const val = lateralized.toLowerCase();
  if (val.includes('left')) return LATERAL_LABELS.left;
  if (val.includes('right')) return LATERAL_LABELS.right;
  return neutral;
}

const clampPct = (n: number | undefined | null): number => {
  const x = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  return Math.max(0, Math.min(100, Math.round(x)));
};

export interface LaymanClassificationDetails {
  patternLabel: string;
  chipLabel: string;
  lateralBadge?: string;
  phase1Goal?: string;
  orientationConfidencePct: number;
  lateralizationConfidencePct: number;
  groundingConfidencePct: number;
  orientationStrategyLabel?: string;
  groundingSideLabel?: string;
  keyMetrics: string[];
  globalReasoning: string[];
  lateralReasoning: string[];
  signals: string[];
  nextStepTitle: string;
  nextStepBody: string;
}

export interface SelfAssessmentReportSections {
  orientation: SummaryCardInfo | null;
  lateral: SummaryCardInfo | null;
  classification: PostureAnalysis['classification'];
  layman: LaymanClassificationDetails | null;
  drills: DrillEntry[];
  views: readonly SelfAssessmentView[];
  captured: PostureAnalysis['captured'];
  exercisesIntro: string;
  disclaimer: string;
}

export const SELF_ASSESSMENT_DISCLAIMER =
  'This report is for educational purposes based on posture screening. It is not medical advice. If you experience pain or have a known injury, please consult a qualified healthcare professional before starting any program.';

export function buildLaymanClassificationDetails(
  classification: NonNullable<PostureAnalysis['classification']>,
  options?: { hideExercises?: boolean },
): LaymanClassificationDetails {
  const latPct = clampPct(classification.lateralizationConfidencePct);
  const grdPct = clampPct(classification.groundingConfidencePct);
  const hide = options?.hideExercises ?? true;

  return {
    patternLabel: getLaymanPatternLabel(classification.pattern),
    chipLabel: getLaymanPatternChipLabel(classification.pattern),
    lateralBadge: getLaymanLateralBadge(classification.lateralized),
    phase1Goal: getLaymanPhase1Goal(classification.phase1Goal),
    orientationConfidencePct: clampPct(classification.orientationConfidencePct),
    lateralizationConfidencePct: latPct,
    groundingConfidencePct: grdPct,
    orientationStrategyLabel: getLaymanStrategyLabel(classification.classificationStrategyLabel),
    groundingSideLabel: getLaymanGroundingSide(classification.groundingSide),
    keyMetrics: (classification.keyMetrics ?? []).map(getLaymanKeyMetric),
    globalReasoning: (classification.reasoning?.global ?? []).map(getLaymanReasoningLine),
    lateralReasoning: (classification.reasoning?.lateral ?? []).map(getLaymanReasoningLine),
    signals: (classification.signals ?? []).map(getLaymanSignal),
    nextStepTitle: hide
      ? 'Review your posture details'
      : LAYMAN_CLASSIFICATION_UI.nextStepConfirmTitle,
    nextStepBody: hide
      ? 'Review these alignment observations with a qualified movement professional for tailored next steps.'
      : LAYMAN_CLASSIFICATION_UI.nextStepConfirmBody,
  };
}

/**
 * Helper to check if the A/B testing flag to hide exercises (`abtestingnex`) is set in the URL search params.
 * If present (no value required, e.g. `?abtestingnex`), exercise recommendations are excluded.
 */
export function isAbTestingNexActive(): boolean {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(window.location.search);
  if (!searchParams.has('abtestingnex')) return false;
  const val = searchParams.get('abtestingnex');
  return val !== 'false' && val !== '0';
}

export function buildSelfAssessmentReportSections(
  analysis: PostureAnalysis,
  options?: { hideExercises?: boolean },
): SelfAssessmentReportSections {
  const classification = analysis.classification;
  const pattern = classification?.pattern;
  const hideExercises = options?.hideExercises ?? true;

  return {
    orientation: getOrientationInfo(pattern),
    lateral: getLateralInfo(classification?.lateralized, { hideExercises }),
    classification,
    layman: classification ? buildLaymanClassificationDetails(classification, { hideExercises }) : null,
    drills: hideExercises ? [] : getDrillsForPattern(pattern),
    views: SELF_ASSESSMENT_VIEWS,
    captured: analysis.captured,
    exercisesIntro:
      'Start with 2–3 sets of each, 3 times per week. Consistency matters more than intensity.',
    disclaimer: SELF_ASSESSMENT_DISCLAIMER,
  };
}

export function buildSelfAssessmentPdfFilename(contactName?: string, date?: Date): string {
  const safeName = (contactName?.trim() || 'Self_Assessment').replace(/\s+/g, '_');
  const dateStr = date
    ? date.toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  return `${safeName}_Posture_Report_${dateStr}.pdf`;
}
